import { Button } from "@/components/ui/button";
import { Lock, CheckCircle2, Sunrise, Sparkles, RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";
import { useI18n } from "@/lib/i18n";

/**
 * Compact "Receipt Actions" card — the primary act-now surface at the top of
 * Home. Two compact rows (Today's receipt, Weekly receipt), each with a
 * server-derived status and ONE quick action. Quick actions NEVER approve
 * directly: they only open draft generation / the Drafts review (where the
 * approval preview shows "This is what your team will see"). Presentation only —
 * status text + the "N of M approved" denominator are passed in from the server
 * payload; nothing is computed here.
 */
export type ReceiptActionRow = {
  key: string;
  icon: "today" | "weekly";
  label: string;
  /** Server-derived status text, e.g. "Ready to review" / "0 of 5 approved". */
  statusText: string;
  /** Approved AND unchanged since approval (clean). Drives the green check. */
  approved: boolean;
  /**
   * The team currently sees an approved snapshot for this row — true for
   * approved-clean AND post-approval states (new updates captured / changes not
   * shared). Used only for the footer trust copy. The snapshot stays live until
   * re-approval; this never publishes anything.
   */
  teamSeesSnapshot?: boolean;
  /**
   * Approved, but there are UNSHARED updates (new capture or post-approval
   * edits). Renders the status text in an amber "needs your attention" tone —
   * NOT green — so it doesn't read as fully done.
   */
  attention?: boolean;
  /** Single quick action (omitted when approved & clean). Must NOT approve directly. */
  action?: { label: string; onClick: () => void; busy?: boolean };
};

function RowIcon({ icon }: { icon: "today" | "weekly" }) {
  return (
    <span className="h-7 w-7 rounded-lg bg-coral-soft/50 grid place-items-center shrink-0">
      {icon === "today" ? (
        <Sunrise className="h-4 w-4 text-coral" />
      ) : (
        <Sparkles className="h-4 w-4 text-coral" />
      )}
    </span>
  );
}

export function ReceiptActionsCard({
  rows,
  allApproved,
  isPersonal = false,
}: {
  rows: ReceiptActionRow[];
  allApproved: boolean;
  /**
   * The active workspace is a PERSONAL workspace (isPersonal=true). When set,
   * the footer drops team-sharing language ("Visible to team", "Team sees your
   * approved version", "approve & share") because a personal workspace has no
   * team audience — approval just finalizes the receipt for the user. Sharing
   * MECHANICS are unchanged; this is copy only. Property of the workspace the
   * receipt belongs to, NOT of the active-tab admin rule.
   */
  isPersonal?: boolean;
}) {
  const { locale } = useI18n();
  const isAr = locale === "ar";
  return (
    <section className="rounded-3xl border border-border/60 bg-card px-4 py-3.5 shadow-sm" data-testid="receipt-actions">
      <div className="text-[11px] font-semibold tracking-wider text-muted-foreground">
        {isAr ? "إجراءات الملخّص" : "RECEIPT ACTIONS"}
      </div>

      <div className="mt-0.5 divide-y divide-border/60">
        {rows.map(r => (
          <div key={r.key} className="flex items-center gap-2.5 py-2">
            <RowIcon icon={r.icon} />
            <span className="min-w-0 flex-1 leading-tight">
              <span className="block text-sm font-medium truncate">{r.label}</span>
              <span
                className={cn(
                  "block text-xs truncate",
                  r.approved
                    ? "text-emerald-700"
                    : r.attention
                      ? "text-amber-600"
                      : "text-muted-foreground",
                )}
              >
                {r.statusText}
              </span>
            </span>
            <span className="shrink-0">
              {r.approved ? (
                <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              ) : r.action ? (
                <Button
                  onClick={r.action.onClick}
                  disabled={r.action.busy}
                  size="sm"
                  className="h-8 min-w-20 rounded-full shadow-sm shadow-coral/20"
                >
                  {r.action.busy ? "…" : r.action.label}
                </Button>
              ) : null}
            </span>
          </div>
        ))}
      </div>

      {allApproved ? (
        <div className="mt-2 flex items-center gap-1.5 text-xs font-medium text-emerald-700">
          <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
          <span>
            {isPersonal
              ? isAr ? "معتمد · خاص بك" : "Approved · Private to you"
              : isAr ? "معتمد · مرئي للفريق" : "Approved · Visible to team"}
          </span>
        </div>
      ) : rows.length > 0 && rows.every(r => r.approved || r.teamSeesSnapshot) ? (
        // Approved with unshared updates. Amber = needs attention (NOT a clean
        // green). Nothing publishes until the owner re-approves. In a personal
        // workspace there is no team audience, so the copy drops "team".
        <div className="mt-2 flex items-center gap-1.5 text-xs font-medium text-amber-600">
          <RefreshCw className="h-3.5 w-3.5 shrink-0" />
          <span>
            {isPersonal
              ? isAr
                ? "معتمد. أعد الاعتماد لتحديث ملخّصك."
                : "Approved. Re-approve to refresh your receipt."
              : isAr
                ? "يرى الفريق نسختك المعتمدة. أعد الاعتماد لمشاركة التحديثات."
                : "Team sees your approved version. Re-approve to share updates."}
          </span>
        </div>
      ) : (
        <div className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
          <Lock className="h-3.5 w-3.5 shrink-0" />
          <span>
            {isPersonal
              ? isAr ? "خاص بك." : "Private to you."
              : isAr ? "خاص بك حتى تعتمده وتشاركه." : <>Private to you until you approve &amp; share.</>}
          </span>
        </div>
      )}
    </section>
  );
}
